import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { assertOk } from '../../../components/admin/assertOk';
import type { RecommendedDocsApi } from './RecommendedDocsApi';
import type { RecommendedDocEntry, TeamRecommendedDocs } from './types';

export class RecommendedDocsClient implements RecommendedDocsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getCompany(): Promise<RecommendedDocEntry[]> {
    const body = await this.request<{ entries?: RecommendedDocEntry[] }>('/company');
    return body.entries ?? [];
  }

  async setCompany(entries: RecommendedDocEntry[]): Promise<void> {
    await this.send('/company', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });
  }

  async getTeams(teamRefs: string[]): Promise<TeamRecommendedDocs> {
    if (teamRefs.length === 0) {
      return {};
    }
    const search = new URLSearchParams({ refs: teamRefs.join(',') });
    const body = await this.request<{ teams?: TeamRecommendedDocs }>(
      `/teams?${search}`,
    );
    return body.teams ?? {};
  }

  async getTeam(teamRef: string): Promise<RecommendedDocEntry[]> {
    const search = new URLSearchParams({ ref: teamRef });
    const body = await this.request<{ entries?: RecommendedDocEntry[] }>(
      `/team?${search}`,
    );
    return body.entries ?? [];
  }

  async setTeam(teamRef: string, entries: RecommendedDocEntry[]): Promise<void> {
    const search = new URLSearchParams({ ref: teamRef });
    await this.send(`/team?${search}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    });
  }

  private async url(path: string): Promise<string> {
    const base = await this.discoveryApi.getBaseUrl('recommended-docs');
    return `${base}${path}`;
  }

  private async send(path: string, init?: RequestInit): Promise<void> {
    const response = await this.fetchApi.fetch(await this.url(path), init);
    await assertOk(response);
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await this.fetchApi.fetch(await this.url(path), init);
    await assertOk(response);
    return (await response.json()) as T;
  }
}
